import React from 'react'
import { Form, theme } from 'antd'
import { fontSize, radius, spacing } from '@vacationtracker/shared/constants/ThemeSetting'

const GROUP_CLASS = 'vt-days-of-week'
const CHIP_CLASS = 'vt-days-of-week-day'

// The border changes on selection, so it lives in a stylesheet: an inline
// `border` outranks any rule written against it. Colours arrive as custom properties so they
// follow the live theme, dark mode included.
const STYLES = `
.${GROUP_CLASS} { display: flex; flex-wrap: wrap; gap: ${spacing.xxs}px; }
.${CHIP_CLASS} {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 48px;
  height: 32px;
  padding: 0 ${spacing.sm}px;
  border: 1px solid var(--vt-dow-border);
  border-radius: ${radius.base}px;
  background: var(--vt-dow-bg);
  color: var(--vt-dow-color);
  font-size: ${fontSize.base}px;
  cursor: pointer;
  user-select: none;
  transition: border-color 120ms ease-out, color 120ms ease-out;
}
.${CHIP_CLASS}:hover { color: var(--vt-dow-selected); }
.${CHIP_CLASS}[data-selected="true"] {
  border-color: var(--vt-dow-selected);
  color: var(--vt-dow-selected);
}
.${CHIP_CLASS}[data-selected="true"]:hover { border-color: var(--vt-dow-selected-hover); color: var(--vt-dow-selected-hover); }
.${CHIP_CLASS} input {
  position: absolute;
  width: 1px; height: 1px;
  margin: -1px; padding: 0;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
  border: 0;
}
.${CHIP_CLASS}:has(input:focus-visible) { outline: 3px solid var(--vt-dow-focus); outline-offset: 1px; }
.${CHIP_CLASS}:has(input:disabled),
.${CHIP_CLASS}:has(input:disabled):hover {
  cursor: not-allowed;
  border-color: var(--vt-dow-border);
  background: var(--vt-dow-disabled-bg);
  color: var(--vt-dow-disabled-color);
}
.${CHIP_CLASS}[data-selected="true"]:has(input:disabled) { background: var(--vt-dow-disabled-selected-bg); }
@media (prefers-reduced-motion: reduce) { .${CHIP_CLASS} { transition: none; } }
`

export interface DayOfWeekName {
  /** What the chip shows, e.g. "Mon". */
  short: string
  /** What a screen reader announces, e.g. "Monday". */
  full: string
}

export interface DaysOfWeekFieldProps {
  /** Selected days, as the product numbers them: 0 = Sunday to 6 = Saturday. */
  value: number[]
  onChange: (value: number[]) => void
  /** 0 = Sunday, 1 = Monday. The chips read in the order the week runs. */
  firstDayOfWeek: number
  /** Seven names indexed by day number, Sunday first, in the app's language. */
  dayNames: DayOfWeekName[]
  /** Names the set. A `<label>` can point at one control, never at seven. */
  ariaLabel: string
  disabled?: boolean
}

/**
 * The days of the week as seven outline chips; any number can be on. Built for a location's
 * work week, where the selected days are the working days.
 *
 * Underneath it is a checkbox group: each chip is a visually hidden `input[type=checkbox]` in its
 * own `<label>`, so it is tabbable, toggled with Space and announced by its full day name, inside
 * a `role="group"` that carries `ariaLabel`. Takes no strings of its own: pass `dayNames` from
 * the app's i18n (e.g. `dayOfWeekMap` in `constants`).
 *
 * Outline only, drawn like a `SegmentedToggle` button: selected days are primary on the container
 * background, the rest body text. A filled drawing was tried and failed AA.
 */
export function DaysOfWeekField({ value, onChange, firstDayOfWeek, dayNames, ariaLabel, disabled = false }: DaysOfWeekFieldProps) {
  const { token } = theme.useToken()
  // antd paints its own inputs red inside an erroring Form.Item; a custom control has to ask.
  const { status } = Form.Item.useStatus()
  const invalid = status === 'error'

  const order = Array.from({ length: 7 }, (_, index) => (index + firstDayOfWeek) % 7)

  const toggle = (day: number) => {
    onChange(value.includes(day) ? value.filter((candidate) => candidate !== day) : [...value, day].sort((a, b) => a - b))
  }

  const colours = {
    '--vt-dow-border': invalid ? token.colorError : token.colorBorder,
    '--vt-dow-bg': token.colorBgContainer,
    '--vt-dow-color': token.colorText,
    '--vt-dow-selected': token.colorPrimary,
    '--vt-dow-selected-hover': token.colorPrimaryHover,
    '--vt-dow-focus': token.colorPrimaryBorder,
    '--vt-dow-disabled-bg': token.colorBgContainerDisabled,
    '--vt-dow-disabled-color': token.colorTextDisabled,
    '--vt-dow-disabled-selected-bg': token.controlItemBgActiveDisabled,
  } as React.CSSProperties

  return (
    <>
      <style>{STYLES}</style>
      <div className={GROUP_CLASS} role="group" aria-label={ariaLabel} style={colours}>
        {order.map((day) => {
          const selected = value.includes(day)
          return (
            <label key={day} className={CHIP_CLASS} data-selected={selected}>
              <input type="checkbox" checked={selected} disabled={disabled} onChange={() => toggle(day)} aria-label={dayNames[day].full} aria-invalid={invalid || undefined} />
              {dayNames[day].short}
            </label>
          )
        })}
      </div>
    </>
  )
}

export default DaysOfWeekField
